import React, { useEffect, useState, Fragment, createElement } from 'react';
import { AlertTriangleIcon, BadgeCheckIcon, BookOpenIcon, ChevronRightIcon, CircleGaugeIcon, DumbbellIcon, GiftIcon, MailIcon, ShieldCheckIcon, StarIcon, TargetIcon, TrophyIcon } from 'lucide-react';
import { Accordion, type AccordionItemData } from './Accordion';
import { bonuses, modules, socialProof, testimonials } from './siteData';
import { OfferPanel } from './OfferPanel';
const productImage = 'https://cdn.magicpatterns.com/uploads/jr7u3Q1gfyKkAirAubVPSd/ChatGPT_Image_Jul_10,_2026,_06_16_59_PM.png';
const painPoints = [{
  icon: AlertTriangleIcon,
  title: 'Your shoulder aches after serving or hitting',
  text: 'That repeated overhead load can turn a small warning into a season-long problem.'
}, {
  icon: ShieldCheckIcon,
  title: 'You land carefully—but still feel unstable',
  text: 'One bad block landing or awkward step can make you hesitate at the net.'
}, {
  icon: CircleGaugeIcon,
  title: 'Your jump is stuck and your knees pay for it',
  text: 'More reps are not automatically better when landing mechanics and recovery are missing.'
}, {
  icon: TargetIcon,
  title: 'Your hits lack consistency under pressure',
  text: 'When timing slips, power drops and the shoulder often takes the extra stress.'
}, {
  icon: TrophyIcon,
  title: 'You train hard but do not feel game-ready',
  text: 'Without a simple system, it is easy to work more while progressing less.'
}];
const faqItems: AccordionItemData[] = [{
  title: 'Is this only for advanced volleyball players?',
  content: ['No. The system is built for amateur, school, club, and developing players. Each volume helps you apply the right work for your current level.']
}, {
  title: 'What do I receive after I purchase?',
  content: ['You receive immediate digital access to the complete 5-volume PDF system plus the 5 bonus tools included free today.']
}, {
  title: 'Can this replace a doctor or physical therapist?',
  content: ['No. This is an educational performance and prevention system, not medical diagnosis or treatment. Seek qualified medical care for persistent pain or injury.']
}, {
  title: 'Will the files work on my phone?',
  content: ['Yes. The PDFs work on mobile, tablet, or computer, while the interactive Excel tools are best used on a spreadsheet-compatible device.']
}];
type MetaPixelFunction = ((...args: unknown[]) => void) & {
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
};
declare global {
  interface Window {
    fbq?: MetaPixelFunction;
    _fbq?: MetaPixelFunction;
  }
}
export function LandingPage() {
  const [proofIndex, setProofIndex] = useState(0);
  useEffect(() => {
    const pixelId = '1252242806993118';
    const existingScript = document.querySelector(`script[data-meta-pixel="${pixelId}"]`);
    if (existingScript || window.fbq) {
      window.fbq?.('track', 'PageView');
      return;
    }
    const pixelQueue: unknown[][] = [];
    const pixel = ((...args: unknown[]) => {
      pixelQueue.push(args);
    }) as MetaPixelFunction;
    pixel.queue = pixelQueue;
    pixel.loaded = true;
    pixel.version = '2.0';
    window.fbq = pixel;
    window._fbq = pixel;
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.dataset.metaPixel = pixelId;
    document.head.appendChild(script);
  }, []);
  useEffect(() => {
    const interval = window.setInterval(() => {
      setProofIndex(current => (current + 1) % socialProof.length);
    }, 5200);
    return () => window.clearInterval(interval);
  }, []);
  const proof = socialProof[proofIndex];
  return <main className="w-full overflow-hidden bg-white text-[#06172e]" data-id="element-10">
      <section className="relative isolate overflow-hidden bg-[#06172e] px-4 pb-10 pt-5 text-white sm:px-6 sm:pb-14" data-id="element-11">
        <a href="#offer" aria-label="View today’s offer" className="absolute inset-0 -z-20" data-id="element-12">
          <img src={productImage} alt="" className="h-full w-full scale-110 object-cover opacity-20 blur-md" data-id="element-13" />
        </a>
        <div className="absolute inset-0 -z-10 bg-[#06172e]/80" data-id="element-14" />

        <div className="mx-auto max-w-xl" data-id="element-15">
          <div className="pt-10 text-center sm:pt-12" data-id="element-16">
            <p className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-[#f7c948]" data-id="element-17">
              Train smart. Play strong.
            </p>
            <h1 className="mx-auto max-w-lg text-[2.35rem] font-extrabold leading-[1.05] tracking-[-0.035em] sm:text-5xl" data-id="element-18">
              Play Your Best Volleyball Without Paying for It in Pain
            </h1>

            <a href="#offer" className="mx-auto mt-6 block max-w-sm overflow-hidden rounded-2xl border-2 border-[#f7c948] bg-[#102f55] shadow-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f7c948]" data-id="element-19">
              <img src={productImage} alt="Kit Volleyball Without Problems complete digital system" className="w-full" data-id="element-20" />
            </a>

            <p className="mx-auto mt-5 max-w-sm text-base font-semibold leading-relaxed text-white/90 sm:max-w-md" data-id="element-21">
              A complete system for shoulder-smart training and more powerful
              hits.
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-2" data-id="element-22">
              {['📄 100% PDF', '⚡ Instant Delivery', '📚 5 Modules + 5 Bonuses'].map(benefit => <span key={benefit} className="border border-white/25 bg-white/10 px-3 py-2 text-xs font-bold" data-id="element-23">
                  {benefit}
                </span>)}
            </div>

            <a href="#offer" className="mt-7 inline-flex w-full max-w-sm items-center justify-center gap-2 bg-[#f7c948] px-5 py-4 text-base font-black text-[#06172e] shadow-lg transition-colors hover:bg-[#e6b935] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white" data-id="element-24">
              Start Playing Better
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-25" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#991b1b] px-4 py-12 text-white sm:px-6" data-id="element-26">
        <div className="mx-auto max-w-xl" data-id="element-27">
          <p className="text-center text-xs font-black uppercase tracking-[0.18em] text-[#fecaca]" data-id="element-28">
            Common problems for volleyball players
          </p>
          <h2 className="mt-2 text-center text-3xl font-black leading-tight" data-id="element-29">
            You&apos;ve Probably Felt This on the Court
          </h2>

          <div className="mt-7 space-y-3" data-id="element-30">
            {painPoints.map(({
            icon: Icon,
            title,
            text
          }) => <article key={title} className="border-l-4 border-[#fecaca] bg-white p-4 text-[#551010] shadow-sm" data-id="element-31">
                <div className="flex gap-3" data-id="element-32">
                  <Icon className="h-6 w-6 shrink-0 text-[#991b1b]" aria-hidden="true" data-id="element-33" />
                  <div data-id="element-34">
                    <h3 className="font-black" data-id="element-35">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-[#551010]/75" data-id="element-36">
                      {text}
                    </p>
                  </div>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      <section className="bg-[#0c2544] px-4 py-12 text-white sm:px-6" data-id="element-37">
        <div className="mx-auto max-w-xl" data-id="element-38">
          <div className="flex items-center gap-3" data-id="element-39">
            <BookOpenIcon className="h-8 w-8 text-[#f7c948]" aria-hidden="true" data-id="element-40" />
            <div data-id="element-41">
              <p className="text-xs font-black uppercase tracking-[0.17em] text-[#f7c948]" data-id="element-42">
                The system
              </p>
              <h2 className="text-3xl font-black leading-tight" data-id="element-43">
                Introducing Kit Volleyball Without Problems
              </h2>
            </div>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-white/80" data-id="element-44">
            A clear, court-ready framework that connects technique, healthy
            movement, strength, mindset, and sustainable progress.
          </p>

          <div className="mt-7" data-id="element-45">
            <Accordion items={modules} data-id="element-46" />
          </div>

          <a href="#offer" className="mt-7 flex w-full items-center justify-center gap-2 bg-[#f7c948] px-5 py-4 font-black text-[#06172e] transition-colors hover:bg-[#e6b935] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white" data-id="element-47">
            Get the Complete System
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-48" />
          </a>
        </div>
      </section>

      <section className="bg-white px-4 py-12 sm:px-6" aria-labelledby="reviews-title" data-id="element-49">
        <div className="mx-auto max-w-xl" data-id="element-50">
          <p className="text-center text-xs font-black uppercase tracking-[0.18em] text-[#06172e]/65" data-id="element-51">
            From American volleyball players
          </p>
          <h2 id="reviews-title" className="mt-2 text-center text-3xl font-black" data-id="element-52">
            Built for Real Rotations, Real Goals.
          </h2>

          <div className="mt-7 space-y-3" data-id="element-53">
            {testimonials.map(testimonial => <figure key={testimonial.name} className="border border-[#06172e]/10 bg-white p-5 shadow-sm" data-id="element-54">
                <div className="flex gap-1 text-[#f7c948]" aria-label="5 out of 5 stars" data-id="element-55">
                  {Array.from({
                length: 5
              }).map((_, index) => <StarIcon key={index} className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-56" />)}
                </div>
                <blockquote className="mt-3 text-sm font-medium leading-relaxed text-[#06172e]/80" data-id="element-57">
                  “{testimonial.quote}”
                </blockquote>
                <figcaption className="mt-3 border-t border-[#06172e]/10 pt-3 text-xs" data-id="element-58">
                  <strong className="font-black text-[#06172e]" data-id="element-59">
                    {testimonial.name}
                  </strong>
                  <span className="mt-0.5 block text-[#06172e]/60" data-id="element-60">
                    {testimonial.meta}
                  </span>
                </figcaption>
              </figure>)}
          </div>
        </div>
      </section>

      <section className="bg-[#102f55] px-4 py-12 text-white sm:px-6" data-id="element-61">
        <div className="mx-auto max-w-xl" data-id="element-62">
          <div className="flex items-start gap-3" data-id="element-63">
            <GiftIcon className="h-8 w-8 shrink-0 text-[#f7c948]" aria-hidden="true" data-id="element-64" />
            <div data-id="element-65">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#f7c948]" data-id="element-66">
                Included free today
              </p>
              <h2 className="text-3xl font-black leading-tight" data-id="element-67">
                Train More Efficiently With These Free Bonuses
              </h2>
            </div>
          </div>

          <p className="mt-4 text-sm font-semibold leading-relaxed text-white/85" data-id="element-68">
            Five practical tools that turn the system into a week-by-week
            plan—free with today&apos;s offer.
          </p>

          <div className="mt-7" data-id="element-69">
            <Accordion items={bonuses} data-id="element-70" />
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-12 sm:px-6" aria-labelledby="comparison-title" data-id="element-71">
        <div className="mx-auto max-w-xl" data-id="element-72">
          <h2 id="comparison-title" className="text-center text-3xl font-black leading-tight" data-id="element-73">
            Without the Kit vs. With the Kit
          </h2>

          <div className="mt-7 overflow-hidden border border-[#06172e]/15 bg-white shadow-sm" data-id="element-74">
            <div className="grid grid-cols-2 text-center text-sm" data-id="element-75">
              <div className="bg-[#991b1b] px-2 py-3 font-black text-white" data-id="element-76">
                Without the Kit
              </div>
              <div className="bg-[#0c2544] px-2 py-3 font-black text-white" data-id="element-77">
                With the Kit
              </div>
              {[['Guessing through pain', 'Clear movement protocols'], ['Random training volume', '12-week progressions'], ['Technique in isolation', 'Technique + shoulder health'], ['Pressure without a routine', 'Reset tools for every rotation']].map(([withoutKit, withKit]) => <Fragment key={withoutKit} data-id="element-78">
                  <div className="border-r border-t border-[#fecaca] bg-[#fff7f7] px-2 py-3 text-xs leading-snug text-[#991b1b]" data-id="element-79">
                    {withoutKit}
                  </div>
                  <div className="flex items-center justify-center gap-1 border-t border-[#06172e]/10 px-2 py-3 text-xs font-bold leading-snug text-[#06172e]" data-id="element-80">
                    <BadgeCheckIcon className="h-3.5 w-3.5 shrink-0 text-[#06172e]" aria-hidden="true" data-id="element-81" />
                    {withKit}
                  </div>
                </Fragment>)}
            </div>
          </div>

          <a href="#offer" className="mt-7 flex w-full items-center justify-center gap-2 bg-[#06172e] px-5 py-4 font-black text-white transition-colors hover:bg-[#0c2544] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#06172e]" data-id="element-82">
            Choose the Complete System
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-83" />
          </a>
        </div>
      </section>

      <OfferPanel data-id="element-84" />

      <section className="bg-white px-4 py-12 sm:px-6" aria-labelledby="faq-title" data-id="element-85">
        <div className="mx-auto max-w-xl" data-id="element-86">
          <p className="text-center text-xs font-black uppercase tracking-[0.18em] text-[#06172e]/65" data-id="element-87">
            Before you decide
          </p>
          <h2 id="faq-title" className="mt-2 text-center text-3xl font-black" data-id="element-88">
            Frequently Asked Questions
          </h2>

          <div className="mt-7" data-id="element-89">
            <Accordion items={faqItems} variant="light" data-id="element-90" />
          </div>

          <div className="mt-10 border-t border-[#06172e]/15 pt-7 text-center" data-id="element-91">
            <DumbbellIcon className="mx-auto h-8 w-8 text-[#06172e]" aria-hidden="true" data-id="element-92" />
            <p className="mt-3 text-sm font-bold text-[#06172e]/70" data-id="element-93">
              Need support?
            </p>
            <a href="mailto:EbookDreamShop@gmail.com" className="mt-1 inline-flex items-center gap-2 font-black text-[#06172e] underline decoration-[#f7c948] decoration-2 underline-offset-4" data-id="element-94">
              <MailIcon className="h-4 w-4 text-[#06172e]" aria-hidden="true" data-id="element-95" />
              EbookDreamShop@gmail.com
            </a>
          </div>
        </div>
      </section>

      <aside aria-live="polite" className="fixed bottom-4 left-4 right-4 z-30 mx-auto max-w-sm border-l-4 border-[#f7c948] bg-white p-3 shadow-2xl sm:left-auto sm:right-5" data-id="element-96">
        <p className="text-[10px] font-black uppercase tracking-wider text-[#06172e]/65" data-id="element-97">
          Recent purchase
        </p>
        <p className="mt-0.5 text-sm font-bold text-[#06172e]" data-id="element-98">
          {proof[0]}{' '}
          <span className="font-normal text-[#06172e]/65" data-id="element-99">
            from {proof[1]} just got the digital kit.
          </span>
        </p>
      </aside>
    </main>;
}